import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import ogHome from "@/assets/og/og-home.jpg";
import ogSell from "@/assets/og/og-sell.jpg";
import ogGrow from "@/assets/og/og-grow.jpg";
import ogServices from "@/assets/og/og-services.jpg";
import ogPricing from "@/assets/og/og-pricing.jpg";
import ogLearn from "@/assets/og/og-learn.jpg";

const SITE_URL = "https://sell.raneen.com";

// Section -> OG image. Routes are matched by their top-level segment.
const SECTION_IMAGES: Record<string, string> = {
  "/": ogHome,
  "/sell": ogSell,
  "/grow": ogGrow,
  "/services": ogServices,
  "/pricing": ogPricing,
  "/learn": ogLearn,
};

function resolveOgImage(pathWithoutLang: string): string {
  if (pathWithoutLang === "/") return SECTION_IMAGES["/"];
  const top = "/" + pathWithoutLang.split("/").filter(Boolean)[0];
  return SECTION_IMAGES[top] ?? ogHome;
}
const BRAND_EN = "raneen Sellers";
const BRAND_AR = "بائعو رنين";

type Meta = { title: { en: string; ar: string }; description: { en: string; ar: string }; keywords?: { en: string; ar: string } };

const META: Record<string, Meta> = {
  "/": {
    title: {
      en: "Sell on raneen — Launch & Grow Your Online Business in Egypt",
      ar: "بيع على رنين — ابدأ ونمّي بزنسك أونلاين في مصر",
    },
    description: {
      en: "Become a raneen seller and reach millions of Egyptian shoppers. Easy signup, FBR fulfillment across Egypt, ads, and dedicated support.",
      ar: "ابقى بائع على رنين ووصّل لملايين المتسوقين في مصر. تسجيل سهل، شحن FBR لكل مصر، إعلانات، أدوات للبراند، ودعم بائعين متخصص.",
    },
    keywords: {
      en: "sell on raneen, become a seller in Egypt, raneen sellers, e-commerce Egypt, online selling Egypt, FBR fulfillment, raneen marketplace",
      ar: "البيع على رنين، بائع رنين، تجارة إلكترونية في مصر، البيع أونلاين في مصر، خدمة FBR، سوق رنين",
    },
  },
  "/sell": {
    title: {
      en: "How to Sell on raneen — Open Your Seller Account in Egypt",
      ar: "ازاي تبيع على رنين — افتح حساب بائع في مصر",
    },
    description: {
      en: "Open your raneen seller account in minutes. Step-by-step guide to register, list products, set pricing, and ship your first order in Egypt.",
      ar: "افتح حساب بائع رنين في دقايق. دليل خطوة بخطوة للتسجيل، رفع المنتجات، التسعير، وشحن أول طلب ليك في أي مكان في مصر.",
    },
  },
  "/sell/what-to-sell": {
    title: {
      en: "What to Sell on raneen — Pick Profitable Products in Egypt",
      ar: "إيه اللي تبيعه على رنين — اختار منتجات مربحة في مصر",
    },
    description: {
      en: "Find profitable products to sell on raneen. Discover trending categories, demand signals, and research tips for Egyptian shoppers.",
      ar: "لاقي منتجات مربحة تبيعها على رنين. اكتشف الفئات الرائجة، إشارات الطلب، ونصايح بحث المنتجات مخصصة للمتسوقين المصريين.",
    },
  },
  "/sell/listing-products": {
    title: {
      en: "List Products on raneen — Build Pages That Convert",
      ar: "رفع المنتجات على رنين — اعمل صفحات تبيع",
    },
    description: {
      en: "Learn how to list products on raneen with high-converting titles, images, variants, and attributes. Best practices to boost visibility and sales.",
      ar: "اتعلم ازاي ترفع منتجاتك على رنين بعناوين وصور ومواصفات تزود الظهور والمبيعات. أفضل الممارسات لرفع منتج محترف.",
    },
  },
  "/sell/product-categories": {
    title: {
      en: "Product Categories on raneen — Browse Supported Categories",
      ar: "فئات المنتجات على رنين — تصفح التصنيفات المدعومة",
    },
    description: {
      en: "Browse all product categories available for sellers on raneen Egypt. Find the right category and commission rate for your inventory.",
      ar: "تصفح كل فئات المنتجات المتاحة لبائعي رنين في مصر. لاقي التصنيف المناسب ونسبة العمولة لمنتجاتك.",
    },
  },
  "/sell/product-details": {
    title: {
      en: "Product Detail Pages on raneen — Win the Buy Box",
      ar: "صفحات تفاصيل المنتج على رنين — اكسب البيع",
    },
    description: {
      en: "Build product detail pages that convert: winning titles, bullet points, images, A+ Content, and attributes that win the raneen buy box.",
      ar: "اعمل صفحات تفاصيل منتج تبيع: عناوين قوية، نقاط، صور، محتوى A+، ومواصفات تكسب البيع على رنين.",
    },
  },
  "/sell/pricing-products": {
    title: {
      en: "Price Products on raneen — Margin & Promotion Strategy",
      ar: "تسعير منتجاتك على رنين — استراتيجية الهامش والعروض",
    },
    description: {
      en: "Price competitively on raneen: calculate margins after commission and FBR fees, build pricing strategy, and run promotions that drive sales.",
      ar: "سعّر منتجاتك على رنين بشكل تنافسي: احسب الهامش بعد العمولة ورسوم FBR، ابني استراتيجية تسعير، وشغّل عروض تزود مبيعاتك.",
    },
  },
  "/sell/fulfillment": {
    title: {
      en: "Fulfillment on raneen — FBR, Self-Ship & Supply Chain",
      ar: "خيارات الشحن على رنين — FBR، شحن ذاتي، وسلسلة الإمداد",
    },
    description: {
      en: "Compare fulfillment options on raneen: FBR, self-ship, and supply-chain services. Deliver anywhere in Egypt fast and reliably.",
      ar: "قارن خيارات الشحن على رنين: FBR، الشحن الذاتي، وخدمات سلسلة الإمداد. وصّل لأي مكان في مصر بسرعة وبشكل موثوق.",
    },
  },
  "/sell/fbr": {
    title: {
      en: "Fulfilled by raneen (FBR) — Storage, Packing & Delivery",
      ar: "FBR — تخزين وتغليف وتوصيل من رنين",
    },
    description: {
      en: "Let raneen store, pack, and deliver your orders nationwide. Learn how Fulfilled by raneen (FBR) works, pricing, and how to enroll today.",
      ar: "خلّي رنين تخزن وتغلف وتوصّل طلباتك لكل مصر. اعرف ازاي تشتغل خدمة FBR، الأسعار، وازاي تسجّل دلوقتي.",
    },
  },
  "/grow": {
    title: {
      en: "Grow Your raneen Store — Ads, Brand Tools & Incentives",
      ar: "نمّي متجرك على رنين — إعلانات وأدوات براند وحوافز",
    },
    description: {
      en: "Scale your raneen sales with sponsored ads, brand registry, A+ Content, storefronts, and new-seller incentives built for Egyptian sellers.",
      ar: "كبّر مبيعاتك على رنين بالإعلانات الممولة، سجل العلامات، محتوى A+، صفحات المتجر، وحوافز البائعين الجدد المعمولة للبائع المصري.",
    },
  },
  "/grow/advertising": {
    title: {
      en: "Sponsored Ads on raneen — Promote Your Products in Egypt",
      ar: "الإعلانات الممولة على رنين — روّج منتجاتك في مصر",
    },
    description: {
      en: "Reach more shoppers with raneen sponsored product ads. Set budgets, target keywords, and measure ROAS to grow sales across Egypt.",
      ar: "وصّل لعدد أكبر من المتسوقين بإعلانات رنين الممولة. حدد الميزانية، استهدف الكلمات، وقيس العائد لزيادة المبيعات في كل مصر.",
    },
  },
  "/grow/advertising/start": {
    title: {
      en: "Start Advertising on raneen — Launch Your First Campaign",
      ar: "ابدأ الإعلان على رنين — أطلق أول حملة ليك",
    },
    description: {
      en: "Launch your first raneen ad campaign step-by-step: sign in, pick a goal, set a budget, and start driving traffic to your products.",
      ar: "أطلق أول حملة إعلانية على رنين خطوة بخطوة: سجّل دخول، اختار هدفك، حدد ميزانية، وابدأ تجيب زوار لمنتجاتك في مصر.",
    },
  },
  "/grow/advertising/brand": {
    title: {
      en: "Brand Promotion on raneen — Build Brand Awareness in Egypt",
      ar: "الترويج للبراند على رنين — ابني وعي لعلامتك في مصر",
    },
    description: {
      en: "Promote your brand on raneen with sponsored brand ads, storefronts, and offline visibility across 58+ raneen branches in Egypt.",
      ar: "روّج لبراندك على رنين بإعلانات البراند، صفحات المتجر، والظهور في أكتر من 58 فرع رنين في مصر.",
    },
  },
  "/grow/brand-registry": {
    title: {
      en: "Brand Registry on raneen — Protect Your Trademark",
      ar: "سجل العلامات التجارية على رنين — احمي علامتك",
    },
    description: {
      en: "Enroll in raneen Brand Registry to protect your trademark, control listings, and unlock advanced brand tools and content features.",
      ar: "سجّل في برنامج Brand Registry على رنين لحماية علامتك، التحكم في منتجاتك، وفتح أدوات ومحتوى متقدم للبراند.",
    },
  },
  "/grow/a-plus-content": {
    title: {
      en: "A+ Content on raneen — Rich Brand Storytelling Pages",
      ar: "محتوى A+ على رنين — صفحات منتجات بحكاية علامتك",
    },
    description: {
      en: "Use A+ Content on raneen to add rich images, comparison tables, and brand stories to your product pages and lift conversion rates.",
      ar: "استخدم محتوى A+ على رنين لإضافة صور، جداول مقارنة، وحكاية علامتك في صفحات المنتجات لزيادة معدل التحويل.",
    },
  },
  "/grow/new-seller-incentives": {
    title: {
      en: "New Seller Incentives — Bonuses for Joining raneen",
      ar: "حوافز البائعين الجدد — مكافآت الانضمام لرنين",
    },
    description: {
      en: "New on raneen? Get welcome incentives, ad credits, FBR discounts, and dedicated onboarding to kickstart your first 90 days of selling.",
      ar: "جديد على رنين؟ احصل على حوافز ترحيب، أرصدة إعلانات، خصومات FBR، ودعم مخصص في أول 90 يوم بيع.",
    },
  },
  "/grow/storefront": {
    title: {
      en: "Brand Storefront on raneen — Free Custom Brand Pages",
      ar: "متجر العلامة على رنين — صفحات مجانية مخصصة لعلامتك",
    },
    description: {
      en: "Build a free custom storefront on raneen to showcase your catalog, tell your brand story, and convert raneen shoppers into loyal customers.",
      ar: "ابني متجر مجاني مخصص لعلامتك على رنين لعرض كتالوجك، حكي حكاية البراند، وتحويل متسوقي رنين لعملاء دايمين.",
    },
  },
  "/services/supply-chain": {
    title: {
      en: "Supply Chain Services — raneen Logistics for Sellers",
      ar: "خدمات سلسلة الإمداد — لوجستيات رنين للبائعين",
    },
    description: {
      en: "Move inventory from your supplier to raneen fulfillment centers across Egypt with our integrated supply chain services and seller logistics.",
      ar: "انقل بضاعتك من المورّد لمراكز شحن رنين في كل مصر باستخدام خدمات سلسلة الإمداد ولوجستيات البائعين المتكاملة.",
    },
  },
  "/services/multi-channel": {
    title: {
      en: "Multi-Channel Fulfillment — Ship Any Channel from raneen",
      ar: "الشحن متعدد القنوات — وصّل من أي قناة عبر رنين",
    },
    description: {
      en: "Use raneen's fulfillment network to ship orders from your own website or other channels anywhere in Egypt, fast and reliably.",
      ar: "استخدم شبكة شحن رنين لتوصيل طلبات موقعك أو أي قناة تانية لأي مكان في مصر بسرعة وبشكل موثوق.",
    },
  },
  "/pricing": {
    title: {
      en: "raneen Seller Pricing — Plans, Commissions & Fees",
      ar: "أسعار بائعي رنين — الخطط، العمولات، والرسوم",
    },
    description: {
      en: "See raneen seller subscription plans, category commission rates, and FBR fees. Transparent pricing to help you pick the right plan for your business.",
      ar: "شوف خطط اشتراك بائعي رنين، نسب العمولة لكل فئة، ورسوم FBR. أسعار شفافة تساعدك تختار الخطة المناسبة لبزنسك.",
    },
  },
  "/pricing/optional-costs": {
    title: {
      en: "Optional Seller Costs on raneen — Add-on Service Fees",
      ar: "تكاليف اختيارية للبائعين على رنين — رسوم الخدمات الإضافية",
    },
    description: {
      en: "Review optional add-on costs for raneen sellers — advertising, premium fulfillment, brand services, and A+ Content — to plan your full budget.",
      ar: "اطّلع على التكاليف الاختيارية لبائعي رنين — الإعلانات، الشحن المميز، خدمات البراند، ومحتوى A+ — لتخطيط ميزانيتك الكاملة.",
    },
  },
  "/learn": {
    title: {
      en: "raneen Seller University — Guides, Webinars & Stories",
      ar: "أكاديمية بائعي رنين — أدلة وندوات وقصص نجاح",
    },
    description: {
      en: "Learn to sell on raneen with free guides, live webinars, and real Egyptian seller success stories. Everything you need to grow your e-commerce business.",
      ar: "اتعلم البيع على رنين بأدلة مجانية، ندوات مباشرة، وقصص نجاح بائعين مصريين حقيقيين. كل اللي تحتاجه عشان تكبّر بزنسك الإلكتروني.",
    },
  },
  "/learn/webinars": {
    title: {
      en: "raneen Seller Webinars — Live Training & On-Demand",
      ar: "ندوات بائعي رنين — تدريب مباشر ومسجل",
    },
    description: {
      en: "Join free live and on-demand webinars on listing, advertising, fulfillment, and growth strategies for raneen sellers in Egypt.",
      ar: "احضر ندوات مجانية مباشرة ومسجلة عن رفع المنتجات، الإعلانات، الشحن، واستراتيجيات النمو لبائعي رنين في مصر.",
    },
  },
  "/learn/seller-stories": {
    title: {
      en: "Seller Success Stories — Real raneen Sellers in Egypt",
      ar: "قصص نجاح البائعين — بائعو رنين الحقيقيين في مصر",
    },
    description: {
      en: "Read how Egyptian entrepreneurs grew their businesses on raneen. Real success stories, growth numbers, and lessons from top sellers across Egypt.",
      ar: "اقرأ ازاي رواد أعمال مصريين كبّروا مشاريعهم على رنين. قصص نجاح حقيقية، أرقام نمو، ودروس من أفضل البائعين في مصر.",
    },
  },
  "/learn/seller-event": {
    title: {
      en: "raneen Seller Summit 2026 — Watch the Event Recap",
      ar: "قمة بائعي رنين 2026 — اتفرج على ملخص الحدث",
    },
    description: {
      en: "Watch the raneen Seller Summit 2026 recap: 1,200+ sellers, 12 workshops, 3 major awards, and stories from Egypt's top e-commerce sellers.",
      ar: "اتفرج على ملخص قمة بائعي رنين 2026: أكتر من 1200 بائع، 12 ورشة عمل، 3 جوايز كبرى، وقصص من أفضل بائعي التجارة الإلكترونية في مصر.",
    },
    keywords: {
      en: "raneen seller summit, raneen seller event 2026, Egypt e-commerce conference, seller awards Egypt",
      ar: "قمة بائعي رنين، حدث بائعي رنين 2026، مؤتمر التجارة الإلكترونية في مصر، جوايز البائعين",
    },
  },
  "/learn/seller-stories/dukan-alaa": {
    title: {
      en: "Dukan Alaa — 97% Growth on raneen | Seller Story",
      ar: "دكان علاء — نمو 97% على رنين | قصة نجاح",
    },
    description: {
      en: "See how Alaa El Din grew Dukan Alaa by 97% year-over-year using AI tools in raneen's new seller dashboard. Read the full success story.",
      ar: "شوف ازاي علاء الدين كبّر دكان علاء بنسبة 97% سنويًا باستخدام أدوات الذكاء الاصطناعي في لوحة تحكم رنين الجديدة. اقرأ القصة كاملة.",
    },
  },
  "/learn/seller-stories/craft-furniture": {
    title: {
      en: "Craft Furniture — 233% Growth on raneen | Seller Story",
      ar: "كرافت للأثاث — نمو 233% على رنين | قصة نجاح",
    },
    description: {
      en: "See how Craft Furniture grew sales 233% year-over-year on raneen and won at the raneen Seller Summit 2026. Read the full success story.",
      ar: "شوف ازاي كرافت للأثاث حقق نمو 233% في المبيعات على رنين وفاز في قمة بائعي رنين 2026. اقرأ القصة كاملة.",
    },
    keywords: {
      en: "Craft Furniture, raneen seller, success story, Egypt e-commerce, seller summit 2026",
      ar: "كرافت للأثاث، بائع رنين، قصة نجاح، تجارة إلكترونية مصر، قمة البائعين 2026",
    },
  },
  "/learn/seller-stories/al-andalos-air-conditioner": {
    title: {
      en: "Al Andalos AC — 160% Growth on raneen | Seller Story",
      ar: "الأندلس للتكييفات — نمو 160% على رنين | قصة نجاح",
    },
    description: {
      en: "Watch how Zaid Ali grew Al Andalos for Air Conditioner by 160% year-over-year on raneen Marketplace. Local shop, nationwide reach via FBR.",
      ar: "شوف ازاي زيد علي حقق نمو 160% سنويًا للأندلس للتكييفات على رنين ماركت بليس. محل محلي وصل لكل مصر عن طريق FBR.",
    },
  },
  "/learn/faq": {
    title: {
      en: "raneen Sellers FAQ — Registration, Fees & Fulfillment",
      ar: "أسئلة بائعي رنين الشائعة — التسجيل، الرسوم، والشحن",
    },
    description: {
      en: "Answers to the most common questions about selling on raneen: registration, commissions, FBR fulfillment, payouts, and dedicated seller support.",
      ar: "إجابات لأكتر الأسئلة شيوعًا عن البيع على رنين: التسجيل، العمولات، شحن FBR، التحويلات، ودعم البائعين المخصص.",
    },
  },
  "/get-app": {
    title: {
      en: "Download the raneen Seller App — iOS & Android",
      ar: "حمّل تطبيق بائعي رنين — iOS و Android",
    },
    description: {
      en: "Get the free raneen Seller App for iPhone and Android. Manage orders, track sales, and control your inventory on the go from anywhere in Egypt.",
      ar: "حمّل تطبيق بائعي رنين المجاني للآيفون والأندرويد. دير الطلبات، تابع المبيعات، وتحكم في المخزون من أي مكان في مصر.",
    },
  },
  "/seller-app": {
    title: {
      en: "raneen Seller App — Coming Soon on iOS & Android",
      ar: "تطبيق بائعي رنين — قريبًا على iOS و Android",
    },
    description: {
      en: "The raneen Seller App is launching soon for iPhone and Android. Manage orders, inventory, and sales on the go — register as a seller to get notified at launch.",
      ar: "تطبيق بائعي رنين قادم قريبًا للآيفون والأندرويد. دير الطلبات، المخزون، والمبيعات من أي مكان — سجّل كبائع عشان نبلغك أول ما ينزل.",
    },
    keywords: {
      en: "raneen seller app, raneen mobile app, sell on raneen app, iOS Android seller app Egypt",
      ar: "تطبيق بائعي رنين، تطبيق رنين للموبايل، البيع على رنين، تطبيق بائعين مصر",
    },
  },
  "/leadership": {
    title: {
      en: "Leadership Team — Meet the People Behind raneen Sellers",
      ar: "فريق القيادة — تعرّف على اللي ورا بائعي رنين",
    },
    description: {
      en: "Meet the leaders and advisors building raneen Sellers and growing Egypt's e-commerce ecosystem for local entrepreneurs and brands.",
      ar: "تعرّف على القيادات والمستشارين اللي بيبنوا بائعي رنين وبيكبّروا منظومة التجارة الإلكترونية في مصر لرواد الأعمال والبراندات المحلية.",
    },
  },
  "/partners-program": {
    title: {
      en: "Become a raneen Partner — Brands, Agencies & Service Providers",
      ar: "كن شريكًا لرنين — البراندات والوكالات ومزودي الخدمات",
    },
    description: {
      en: "Join the raneen Partner Program. Apply as a brand, agency, fulfillment, financial solution, or service provider and grow with Egypt's leading marketplace.",
      ar: "انضم لبرنامج شركاء رنين. قدّم كبراند، وكالة، شركة شحن، حل مالي، أو مزود خدمة، واكبر مع أكبر سوق إلكتروني في مصر.",
    },
    keywords: {
      en: "raneen partners, partner program Egypt, brand partner, agency partner, fulfillment partner Egypt",
      ar: "شركاء رنين، برنامج الشركاء، شريك براند، شريك وكالة، شريك شحن في مصر",
    },
  },
  "/partner-status": {
    title: {
      en: "Check Partner Application Status — raneen Partners",
      ar: "تابع حالة طلب الشراكة — شركاء رنين",
    },
    description: {
      en: "Track the status of your raneen Partner Program application using your reference number and email.",
      ar: "تابع حالة طلب الانضمام لبرنامج شركاء رنين باستخدام رقم المرجع والإيميل.",
    },
  },
  "/login": {
    title: {
      en: "Sign In to raneen Sellers — Access Your Seller Account",
      ar: "تسجيل الدخول لبائعي رنين — ادخل على حساب البائع",
    },
    description: {
      en: "Sign in to your raneen Seller account to manage orders, inventory, ads, and payouts, or choose how to start selling on raneen Egypt.",
      ar: "سجّل دخول لحسابك على بائعي رنين عشان تدير الطلبات، المخزون، الإعلانات، والتحويلات، أو اختار ازاي تبدأ البيع على رنين في مصر.",
    },
  },
  "/trust": {
    title: {
      en: "Trust & Safety at raneen Sellers — How We Protect You",
      ar: "الأمان والثقة في بائعي رنين — ازاي بنحميك",
    },
    description: {
      en: "How raneen Sellers protects your account, payments, and data with verified onboarding, secure payouts, and buyer-seller protection in Egypt.",
      ar: "اعرف ازاي بائعي رنين بيحميلك حسابك، مدفوعاتك، وبياناتك بتسجيل موثّق، تحويلات آمنة، وحماية بين البائع والمشتري في مصر.",
    },
  },
  "/sell/register": {
    title: {
      en: "Register as a raneen Seller — Open Your Account in Minutes",
      ar: "سجّل كبائع على رنين — افتح حسابك في دقايق",
    },
    description: {
      en: "Complete raneen seller registration: verify your identity, add store details, upload documents, and submit to start selling across Egypt.",
      ar: "املأ نموذج تسجيل بائعي رنين: اتحقق من هويتك، ضيف بيانات متجرك، ارفع المستندات، وقدّم الطلب عشان تبدأ البيع في مصر.",
    },
  },
  "/sell/register/success": {
    title: {
      en: "Registration Submitted — Welcome to raneen Sellers",
      ar: "تم استلام طلبك — أهلًا بيك في بائعي رنين",
    },
    description: {
      en: "Your raneen seller application is in. We'll review your documents and email you next steps so you can list products and start selling in Egypt.",
      ar: "وصلنا طلب تسجيلك كبائع على رنين. هنراجع مستنداتك ونبعتلك الخطوات الجاية على الإيميل عشان ترفع منتجاتك وتبدأ البيع في مصر.",
    },
  },
};

const FALLBACK: Meta = {
  title: {
    en: `${BRAND_EN} — Sell Online in Egypt`,
    ar: `${BRAND_AR} — بيع أونلاين في مصر`,
  },
  description: {
    en: "Become a raneen seller and grow your online business in Egypt with seller tools, FBR fulfillment, and dedicated support.",
    ar: "ابقى بائع على رنين ونمّي بزنسك الإلكتروني في مصر بأدوات بيع وشحن FBR ودعم متخصص.",
  },
};

const SEO = () => {
  const { lang, dir } = useLanguage();
  const location = useLocation();

  const pathWithoutLang = location.pathname.replace(/^\/(ar|en)/, "") || "/";
  const meta = META[pathWithoutLang] ?? FALLBACK;

  const title = meta.title[lang];
  const description = meta.description[lang];
  const keywords = meta.keywords?.[lang];
  const canonical = `${SITE_URL}/${lang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
  const ogType = pathWithoutLang === "/" ? "website" : "article";
  const ogImagePath = resolveOgImage(pathWithoutLang);
  const ogImageUrl = ogImagePath.startsWith("http") ? ogImagePath : `${SITE_URL}${ogImagePath}`;

  return (
    <Helmet>
      <html lang={lang} dir={dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="raneen Sellers" />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={lang === "ar" ? "ar_EG" : "en_US"} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <link rel="alternate" hrefLang="ar" href={`${SITE_URL}/ar${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}/en${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/ar${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />
    </Helmet>
  );
};

export default SEO;
